import { useContext } from 'react';
import { graphql } from 'react-relay';
import { useQuery } from 'relay-hooks';

import { TeacherContext } from 'ms-pages/Teacher/TeacherContext/index';
import { InvariantViolation } from 'ms-utils/app-logging';
import { RELAY_CONNECTION_MAX } from 'ms-utils/relay';

import type { useTeacherContextDataQuery } from './__generated__/useTeacherContextDataQuery.graphql';
import type { TeacherContext as TeacherContextType } from './types';

type NonMaybeTeacherContextType = TeacherContextType & {
  classId: string;
};

function assertIsNonMaybeTeacherContext(
  context: TeacherContextType,
): asserts context is NonMaybeTeacherContextType {
  if (context.classId == null) {
    throw new InvariantViolation(
      `Teacher ${context.teacherId} does not have any classes`,
    );
  }
}

export default function useTeacherContext() {
  const context = useContext(TeacherContext);
  assertIsNonMaybeTeacherContext(context);
  return context;
}

export function useMaybeTeacherContext() {
  return useContext(TeacherContext);
}

const query = graphql`
  query useTeacherContextDataQuery(
    $numberOfSchools: Int!
    $numberOfClasses: Int!
  ) {
    viewer {
      profile {
        ... on Teacher {
          id
          schools(limit: $numberOfSchools) {
            id
            title
            district {
              id
              schoolsCount
            }
            classes(first: $numberOfClasses) {
              edges {
                node {
                  id
                  hasTasks
                }
              }
            }
          }
          teacherPreference {
            id
            createTaskTargetMastery
            onboardingSetup
            dismissedOnboardingFeatures
          }
        }
      }
    }
  }
`;

export function useTeacherContextData() {
  const { error, props, retry } = useQuery<useTeacherContextDataQuery>(
    query,
    {
      numberOfSchools: RELAY_CONNECTION_MAX,
      numberOfClasses: RELAY_CONNECTION_MAX,
    },
    {
      fetchPolicy: 'store-and-network',
    },
  );

  return { error, props, retry };
}
